import { useState } from 'react'
import type { FieldKey } from '@esign/contracts'
import {
  Alert,
  BankCard,
  Button,
  Card,
  Field,
  MailIcon,
  PhoneIcon,
  IdCardIcon,
  MapPinIcon,
  UserIcon,
  SecurityNote,
  Sheet,
} from '@esign/ui'
import { errorText, initialValues, isValidEmail, isValidPhone, isInfoReady } from '../fields.js'
import type { StepProps } from '../types.js'

export function InfoStep({ step, context, error, busy, submit }: StepProps<'info'>) {
  const [values, setValues] = useState<Record<string, string>>(() => {
    let savedIdentity: Record<string, string> = {}
    if (typeof sessionStorage !== 'undefined' && context.session.id) {
      const isDemo = context.session.id === 'ekyc_TzVPZs7eSY9SHOfNlBMM'
      const str =
        sessionStorage.getItem(`dev_identity_data_${context.session.id}`) ||
        (isDemo ? sessionStorage.getItem('dev_identity_data') : null)
      if (str) {
        try {
          savedIdentity = JSON.parse(str)
        } catch {}
      }
    }

    const init = initialValues(context.subject)
    const fallbackBankName = (context.subject as any)['bank_name'] || (context.subject as any)['bank'] || context.partner?.name || ''
    const fallbackAccount = (context.subject as any)['bank_account'] || (context.subject as any)['account_number'] || ''
    const fallbackHolder = (context.subject as any)['account_name'] || context.subject['full_name'] || ''
    const fallbackBranch = (context.subject as any)['bank_branch'] || (context.subject as any)['branch'] || ''

    return {
      ...init,
      email: savedIdentity.email || init.email || '',
      phone: savedIdentity.phone || savedIdentity.phone_number || init.phone || '',
      bank_name: savedIdentity.bank_name || savedIdentity.bank || init.bank_name || fallbackBankName,
      bank_account: savedIdentity.bank_account || savedIdentity.account_number || init.bank_account || fallbackAccount,
      account_name: savedIdentity.account_name || savedIdentity.bank_account_name || init.account_name || fallbackHolder,
      bank_branch: savedIdentity.bank_branch || savedIdentity.branch || init.bank_branch || fallbackBranch,
    }
  })

  const [showBankModal, setShowBankModal] = useState(false)
  const [tempBank, setTempBank] = useState({
    bank_name: '',
    bank_account: '',
    account_name: '',
    bank_branch: '',
  })

  const change = (name: FieldKey, value: string) => setValues((prev) => ({ ...prev, [name]: value }))

  const emailVal = values.email ?? ''
  const phoneVal = values.phone ?? ''
  const isEmailValid = isValidEmail(emailVal)
  const isPhoneValid = isValidPhone(phoneVal)
  const canContinue = isInfoReady(emailVal, phoneVal)

  const emailError = emailVal.trim().length > 0 && !isEmailValid ? 'Email không đúng định dạng' : undefined
  const phoneError = phoneVal.trim().length > 0 && !isPhoneValid ? 'Số điện thoại không hợp lệ (10 chữ số)' : undefined

  const isReal = context.session.id !== 'ekyc_TzVPZs7eSY9SHOfNlBMM'
  const bankName = String(
    values.bank_name ||
    (context.subject as any).bank_name ||
    (context.subject as any).bank ||
    (isReal ? '' : (context.partner.name || 'TPBANK')),
  )
  const rawFullName = context.subject.full_name
  const defaultHolder = Array.isArray(rawFullName) ? rawFullName.join(' ') : rawFullName
  const holderName = String(values.account_name || defaultHolder || (isReal ? '' : 'Nguyễn Văn A'))
  const rawAccount = String(
    values.bank_account ||
    values.account_number ||
    (context.subject as any).bank_account ||
    (context.subject as any).account_number ||
    (isReal ? '' : '9999'),
  )
  const accountNumber = maskAccount(rawAccount)

  const handleOpenBankModal = () => {
    setTempBank({
      bank_name: bankName,
      bank_account: rawAccount,
      account_name: holderName,
      bank_branch: values.bank_branch || '',
    })
    setShowBankModal(true)
  }

  const handleSaveBankModal = () => {
    setValues((prev) => ({
      ...prev,
      bank_name: tempBank.bank_name || bankName,
      bank_account: tempBank.bank_account || rawAccount,
      account_number: tempBank.bank_account || rawAccount,
      account_name: tempBank.account_name || holderName,
      bank_branch: tempBank.bank_branch,
    }))
    setShowBankModal(false)
  }

  const handleSubmit = () => {
    if (!canContinue) return

    const payloadToSubmit = {
      ...values,
      email: values.email,
      phone: values.phone,
      phone_number: values.phone,
      bank_name: bankName,
      bank: bankName,
      bank_account: rawAccount,
      account_number: rawAccount,
      account_name: holderName,
      bank_branch: values.bank_branch || '',
      branch: values.bank_branch || '',
      is_modified: '1',
    }

    if (typeof sessionStorage !== 'undefined') {
      const prevDataStr =
        sessionStorage.getItem(`dev_identity_data_${context.session.id}`) ||
        sessionStorage.getItem('dev_identity_data')
      let prevData = {}
      if (prevDataStr) {
        try {
          prevData = JSON.parse(prevDataStr)
        } catch {}
      }
      const merged = {
        ...prevData,
        ...payloadToSubmit,
      }
      sessionStorage.setItem('dev_identity_data', JSON.stringify(merged))
      sessionStorage.setItem(`dev_identity_data_${context.session.id}`, JSON.stringify(merged))
    }

    submit(payloadToSubmit)
  }

  return (
    <>
      <Card>
        {error && <Alert>{errorText(error.code, error.data)}</Alert>}

        <Field
          name="email"
          type="email"
          label="Email"
          placeholder="Nhập email"
          value={emailVal}
          icon={<MailIcon />}
          error={emailError}
          onValueChange={(val) => change('email', val)}
        />

        <Field
          name="phone"
          type="tel"
          inputMode="tel"
          label="Số điện thoại nhận OTP"
          placeholder="Nhập số điện thoại"
          value={phoneVal}
          icon={<PhoneIcon />}
          error={phoneError}
          onValueChange={(val) => change('phone', val)}
        />

        <BankCard
          bankName={bankName}
          holderName={holderName}
          accountNumber={accountNumber}
          onAction={handleOpenBankModal}
        />

        <SecurityNote text={step.config.security_note || 'Thông tin của bạn được hoàn toàn bảo mật, chúng tôi không chia sẻ với bên thứ 3'} />

        <Button disabled={busy || !canContinue} onClick={handleSubmit}>
          {busy ? 'Đang xử lý…' : 'Tiếp tục'}
        </Button>
      </Card>

      {showBankModal && (
        <Sheet
          title="Đổi tài khoản ngân hàng"
          className="sheet--form"
          onClose={() => setShowBankModal(false)}
          body={
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Field
                name="bank_name"
                label="Ngân hàng"
                placeholder="Nhập tên ngân hàng (ví dụ: TPBANK)"
                value={tempBank.bank_name}
                icon={<IdCardIcon />}
                onValueChange={(val) => setTempBank((p) => ({ ...p, bank_name: val }))}
              />
              <Field
                name="bank_account"
                label="Số tài khoản"
                placeholder="Nhập số tài khoản ngân hàng"
                value={tempBank.bank_account}
                icon={<IdCardIcon />}
                onValueChange={(val) => setTempBank((p) => ({ ...p, bank_account: val }))}
              />
              <Field
                name="account_name"
                label="Tên chủ tài khoản"
                placeholder="Nhập tên chủ tài khoản"
                value={tempBank.account_name}
                icon={<UserIcon />}
                onValueChange={(val) => setTempBank((p) => ({ ...p, account_name: val }))}
              />
              <Field
                name="bank_branch"
                label="Chi nhánh (không bắt buộc)"
                placeholder="Nhập chi nhánh ngân hàng"
                value={tempBank.bank_branch}
                icon={<MapPinIcon />}
                onValueChange={(val) => setTempBank((p) => ({ ...p, bank_branch: val }))}
              />
            </div>
          }
        >
          <Button onClick={handleSaveBankModal}>
            Xác nhận
          </Button>
          <Button variant="ghost" onClick={() => setShowBankModal(false)}>
            Hủy
          </Button>
        </Sheet>
      )}
    </>
  )
}

const maskAccount = (value: string): string =>
  value.length > 4 ? `${'*'.repeat(Math.min(8, value.length - 4))}${value.slice(-4)}` : value
